import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SelfCheckInService } from '../../services/selfCheckIn.service';
import { UserService } from '../../services/user.service';
import { Subscription, interval } from 'rxjs';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AppUtility } from '../../common/apputility';
import { VwSelectOption } from '../../shared/ui/searchable-select/searchable-select.component';

@Component({
  selector: 'app-self-check-in-form',
  templateUrl: './selfCheckInForm.component.html',
  styleUrls: ['./selfCheckInForm.component.css']
})
export class SelfCheckInFormComponent implements OnInit, OnDestroy {
  deviceId: number;
  loading = true;
  loadError: string = '';
  accountId: number;
  deviceType: string = '';
  hostType: string = '';
  residentId: number = null;
  employeeId: number = null;
  hostList: any[] = [];
  selectedHostId: number = null;
  hostSearchTerm: string = '';
  showHostDropdown: boolean = false;
  hostSearchResults: any[] = [];
  hostSearchError: boolean = false;
  selectedHostName: string = '';
  countryList: any[] = [];

  // Both dialing code and nationality lists run to 190+ countries — long
  // enough that finding one by scrolling a plain <select> was the actual
  // QA complaint. These feed app-searchable-select instead.
  get dialingCodeOptions(): VwSelectOption[] {
    return this.countryList.map(c => ({ value: c.dialingCode, label: `${c.dialingCode} (${c.countryName})` }));
  }

  get nationalityOptions(): VwSelectOption[] {
    return this.countryList.map(c => ({ value: c.nationality, label: `${c.countryName} (${c.nationality})` }));
  }
  submitting = false;
  submitted = false;
  submitError: string = '';
  selfGuestId: number = null;
  status: string = '';
  message: string = '';
  rejectionReason: string = '';
  qrImage: string = '';
  polling: Subscription = null;
  visitorForm: FormGroup;
  private subscribe: Subscription[] = [];
  isUniversal: boolean = false;
  hostPreset: boolean = false;
  mode: string = 'Walk-In';
  purposeOfVisitPreset: string = '';
  expectedDatePreset: string = '';
  isPreset: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private toaster: ToastrService,
    private selfCheckInService: SelfCheckInService,
    private userService: UserService
  ) {
    this.route.params.subscribe(params => {
      this.deviceId = params['deviceId'];
    });
  }

  ngOnInit(): void {
    this.visitorForm = new FormGroup({
      fullName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      documentType: new FormControl(''),
      documentId: new FormControl('', [Validators.maxLength(30)]),
      contactNo: new FormControl('', [Validators.pattern(/^[0-9]{8,14}$/)]),
      dialingCode: new FormControl(''),
      email: new FormControl('', [Validators.email, Validators.maxLength(50)]),
      address: new FormControl('', [Validators.maxLength(100)]),
      nationality: new FormControl('', Validators.required),
      gender: new FormControl(''),
      mode: new FormControl('Walk-In'),
      purposeOfVisit: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      expectedVehicleNo: new FormControl('', [Validators.pattern('[a-zA-Z0-9 ]*'), Validators.maxLength(20)]),
      expectedVehicleModel: new FormControl('', [Validators.pattern('[a-zA-Z0-9 ]*'), Validators.maxLength(20)])
    });

    if (!this.deviceId) {
      this.loading = false;
      this.loadError = 'Invalid QR code.';
      return;
    }

    this.validateDevice();
    this.getCountryList();
  }

  identityRequired = false;

  // The property's "Visitor Field Configuration": a field is only shown when it is switched on.
  // Primary number and gender are also mandatory then; email and address are just shown.
  fieldConfig = { contactNo: false, email: false, gender: false, address: false };

  setFieldConfig(config: any): void {
    this.fieldConfig = {
      contactNo: !!(config && config.contactNo),
      email: !!(config && config.email),
      gender: !!(config && config.gender),
      address: !!(config && config.address)
    };
    const contact = this.visitorForm.get('contactNo');
    const dialing = this.visitorForm.get('dialingCode');
    const gender = this.visitorForm.get('gender');
    const digits = Validators.pattern(/^[0-9]{8,14}$/);
    contact.setValidators(this.fieldConfig.contactNo ? [Validators.required, digits] : [digits]);
    dialing.setValidators(this.fieldConfig.contactNo ? [Validators.required] : []);
    gender.setValidators(this.fieldConfig.gender ? [Validators.required] : []);
    [contact, dialing, gender].forEach(c => c.updateValueAndValidity());
  }

  // "Document ID verification during check in" is on for this property: the ID
  // fields become mandatory (the server refuses the request without them too).
  setIdentityRequired(required: boolean): void {
    this.identityRequired = required;
    const type = this.visitorForm.get('documentType');
    const id = this.visitorForm.get('documentId');
    if (required) {
      type.setValidators([Validators.required]);
      id.setValidators([Validators.required, Validators.maxLength(30)]);
    } else {
      type.clearValidators();
      id.setValidators([Validators.maxLength(30)]);
    }
    type.updateValueAndValidity();
    id.updateValueAndValidity();
  }

  validateDevice(): void {
    if (!this.deviceId) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.subscribe.push(
      this.selfCheckInService.validateDevice(this.deviceId.toString()).subscribe({
        next: (data: any) => {
          this.loading = false;
          const wrapper = JSON.parse(JSON.stringify(data));
          this.accountId = wrapper.accountId;
          this.setIdentityRequired(wrapper.identityRequired === true);
          this.setFieldConfig(wrapper.fieldConfig);
          this.deviceType = wrapper.type;
          this.hostType = wrapper.hostType || '';
          this.residentId = wrapper.residentId || null;
          this.employeeId = wrapper.employeeId || null;
          this.isUniversal = this.deviceType === 'UNIVERSAL';
          this.hostPreset = this.deviceType === 'NON_UNIVERSAL';

          if (wrapper.isPreset) {
            this.isPreset = true;
            this.purposeOfVisitPreset = wrapper.purposeOfVisit || '';
            this.expectedDatePreset = wrapper.expectedDate || '';
            this.visitorForm.patchValue({
              purposeOfVisit: this.purposeOfVisitPreset
            });
            this.visitorForm.get('purposeOfVisit').disable();
          }

          if (this.isUniversal) {
            this.loadHosts();
          }
        },
        error: (err) => {
          this.loading = false;
          this.loadError = err.error?.message || 'This QR code is not valid or has expired.';
        }
      })
    );
  }

  loadHosts(): void {
    this.subscribe.push(
      this.selfCheckInService.getAllHosts(this.accountId).subscribe({
        next: (data: any) => { this.hostList = data || []; },
        error: (err) => { console.error('Error loading hosts:', err); this.hostList = []; }
      })
    );
  }

  onHostSearchChange(searchTerm: string): void {
    this.hostSearchTerm = searchTerm;
    this.selectedHostId = null;
    this.selectedHostName = '';
    this.hostSearchError = false;
    if (!searchTerm || searchTerm.trim().length === 0 || !this.accountId) {
      this.hostSearchResults = [];
      this.showHostDropdown = false;
      return;
    }
    this.subscribe.push(
      this.selfCheckInService.searchHost(this.accountId, searchTerm).subscribe({
        next: (data: any) => {
          this.hostSearchResults = data || [];
          this.showHostDropdown = true;
        },
        error: (err) => {
          this.hostSearchResults = [];
          this.hostSearchError = true;
          this.showHostDropdown = true;
          console.error('Error searching hosts:', err);
        }
      })
    );
  }

  onHostFocus(): void {
    if (this.hostSearchTerm && this.hostSearchResults.length > 0) {
      this.showHostDropdown = true;
    }
  }

  onHostBlur(): void {
    setTimeout(() => {
      this.showHostDropdown = false;
    }, 200);
  }

  // Prevents the input's blur handler from closing the dropdown
  // before the click/tap on a host item can register.
  onHostSelectMouseDown(event: MouseEvent | TouchEvent, host: any): void {
    event.preventDefault();
    this.selectHost(host);
  }

  selectHost(host: any): void {
    this.selectedHostId = host.id;
    this.selectedHostName = host.fullName;
    this.hostType = host.hostType;
    this.showHostDropdown = false;
    this.hostSearchTerm = host.fullName;
  }

  onChangeMode(event: any): void {
    const mode = event.target.value;
    this.mode = mode;
    this.visitorForm.patchValue({ mode: mode });
  }

  getCountryList(): void {
    this.subscribe.push(
      this.userService.getCountryList('').subscribe({
        next: (data: any) => { this.countryList = data; },
        error: (err) => { console.error('Error loading countries:', err); }
      })
    );
  }

  private invalidFieldNames(): string[] {
    const labels: { [key: string]: string } = {
      fullName: 'Name', documentType: 'Document Type', documentId: 'Identity No', contactNo: 'Contact Number',
      dialingCode: 'Dialing code', nationality: 'Nationality', gender: 'Gender', purposeOfVisit: 'Purpose Of Visit',
      email: 'Email', address: 'Address', expectedVehicleNo: 'Vehicle Number', expectedVehicleModel: 'Vehicle Model'
    };
    return Object.keys(this.visitorForm.controls)
      .filter(key => this.visitorForm.get(key).invalid)
      .map(key => labels[key] || key);
  }

  submitSelfCheckIn(): void {
    if (this.visitorForm.invalid) {
      this.visitorForm.markAllAsTouched();
      this.toaster.error('Please complete: ' + this.invalidFieldNames().join(', ') + '.');
      return;
    }
    if (this.isUniversal && !this.selectedHostId) {
      this.toaster.error('Please select who you are visiting.');
      return;
    }
    const formValue = this.visitorForm.value;
    const details: any = {
      deviceId: this.deviceId,
      accountId: this.accountId,
      ...formValue,
      mode: formValue.mode || 'Walk-In'
    };
    if (this.isUniversal) {
      if (this.hostType === 'RESIDENTIAL') {
        details.residentId = this.selectedHostId;
        details.employeeId = null;
      } else {
        details.employeeId = this.selectedHostId;
        details.residentId = null;
      }
    } else {
      details.residentId = this.residentId;
      details.employeeId = this.employeeId;
    }

    if (details.email !== null && details.email !== undefined && details.email.trim() === '') {
      details.email = null;
    }

    details.image = null;

    if (!AppUtility.isEmptyString(details.mode) && details.mode === 'Drive-In') {
      if (AppUtility.isEmptyString(details.expectedVehicleNo)) {
        details.expectedVehicleNo = null;
      }
    } else {
      details.expectedVehicleNo = null;
      details.expectedVehicleModel = null;
    }

    // Self check-in is for someone physically at the gate right now, not a
    // future booking — "expected date" was a vestige of the regular
    // Add-Visitor form and isn't something the visitor should need to enter.
    details.expectedDate = new Date();

    this.submitting = true;
    this.subscribe.push(
      this.selfCheckInService.submitSelfCheckIn(details).subscribe({
        next: (response: any) => {
          this.submitting = false;
          this.submitted = true;
          this.selfGuestId = response?.selfGuestId || response?.id || null;
          this.status = response?.status || 'PENDING';
          this.message = response?.message || 'Your request is pending approval. Please wait for the host to accept.';
          this.toaster.success('Submitted! Waiting for host approval.');
          if (this.selfGuestId) {
            this.startPolling();
          }
        },
        error: (err) => {
          this.submitting = false;
          this.submitError = err.error?.message || 'Failed to submit. Please try again.';
          this.toaster.error(this.submitError);
        }
      })
    );
  }

  startPolling(): void {
    this.polling = interval(5000).subscribe(() => {
      this.selfCheckInService.getStatus(this.selfGuestId).subscribe({
        next: (data: any) => {
          this.status = data.status || this.status;
          this.message = data.message || this.message;
          if (data.rejectionReason) {
            this.rejectionReason = data.rejectionReason;
          }
          if (data.qrCode) {
            this.qrImage = 'data:image/png;base64,' + data.qrCode;
          }
          if (this.status === 'ACCEPTED' || this.status === 'REJECTED') {
            this.polling.unsubscribe();
          }
        },
        error: (err) => { console.error('Error polling status:', err); }
      });
    });
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(sub => sub.unsubscribe());
    if (this.polling) {
      this.polling.unsubscribe();
    }
  }
}